import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import CreateUserDialog from 'views/common/create-user-dialog';
import SendQuickTemplateDialog from 'views/common/send-quick-template-dialog';

type ChatQuickActionsValue = {
  /** טקסט מהחיפוש — מילוי אוטומטי בשדה טלפון בדיאלוגים */
  openCreateUserDialog: (prefillFromSearch?: string) => void;
  openQuickTemplateDialog: (prefillFromSearch?: string) => void;
};

const ChatQuickActionsContext = createContext<ChatQuickActionsValue | null>(null);

export function ChatQuickActionsProvider({ children }: { children: React.ReactNode }) {
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showQuickTemplate, setShowQuickTemplate] = useState(false);
  const [createUserPrefill, setCreateUserPrefill] = useState('');
  const [quickTemplatePrefill, setQuickTemplatePrefill] = useState('');

  const openCreateUserDialog = useCallback((prefillFromSearch?: string) => {
    setCreateUserPrefill(prefillFromSearch?.trim() ?? '');
    setShowCreateUser(true);
  }, []);

  const openQuickTemplateDialog = useCallback((prefillFromSearch?: string) => {
    setQuickTemplatePrefill(prefillFromSearch?.trim() ?? '');
    setShowQuickTemplate(true);
  }, []);

  const closeCreateUser = useCallback(() => {
    setShowCreateUser(false);
    setCreateUserPrefill('');
  }, []);

  const closeQuickTemplate = useCallback(() => {
    setShowQuickTemplate(false);
    setQuickTemplatePrefill('');
  }, []);

  const value = useMemo(
    () => ({
      openCreateUserDialog,
      openQuickTemplateDialog,
    }),
    [openCreateUserDialog, openQuickTemplateDialog],
  );

  return (
    <ChatQuickActionsContext.Provider value={value}>
      {children}
      <CreateUserDialog open={showCreateUser} onClose={closeCreateUser} initialPhone={createUserPrefill} />
      <SendQuickTemplateDialog
        open={showQuickTemplate}
        onClose={closeQuickTemplate}
        initialPhone={quickTemplatePrefill}
      />
    </ChatQuickActionsContext.Provider>
  );
}

export function useChatQuickActions(): ChatQuickActionsValue {
  const ctx = useContext(ChatQuickActionsContext);
  if (!ctx) {
    throw new Error('useChatQuickActions must be used within ChatQuickActionsProvider');
  }
  return ctx;
}

/** For optional use outside chats (e.g. sidebar) — no-op if provider missing */
export function useChatQuickActionsOptional(): ChatQuickActionsValue | null {
  return useContext(ChatQuickActionsContext);
}
